import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  TextField,
  Label,
  Icon,
  Separator,
  IconButton,
  Dropdown,
  IDropdownOption,
} from '@fluentui/react';
import { IApprovalFlow, IApprovalStep } from '../../shared/models/IApprovalFlow';
import { IApprovalMatrixEntryUI } from '../../shared/models/IApprovalMatrix';
import { approvalFlowApi, approvalMatrixApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';

interface IApprovalFlowPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onApprovalChange?: () => void;
  bestellungId: number;
}

export const ApprovalFlowPanel: React.FC<IApprovalFlowPanelProps> = ({
  isOpen,
  onDismiss,
  onApprovalChange,
  bestellungId,
}) => {
  const { user } = useAuth();
  const [flows, setFlows] = useState<IApprovalFlow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<{ [flowId: number]: string }>({});
  const [gfComments, setGfComments] = useState<{ [positionKey: string]: string }>({});
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [gfActionInProgress, setGfActionInProgress] = useState<string | null>(null);
  const [departments, setDepartments] = useState<IApprovalMatrixEntryUI[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadFlows();
      loadDepartments();
    }
  }, [isOpen, bestellungId]);

  const loadFlows = async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedFlows = await approvalFlowApi.getFlowsForBestellung(bestellungId);
      setFlows(loadedFlows);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Genehmigungsflows');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await approvalMatrixApi.getAll();
      setDepartments(data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Abteilungen:', err);
    }
  };

  const handleCreateFlowsForAll = async () => {
    if (!selectedDepartment) {
      setError('Bitte wählen Sie eine Abteilung aus');
      return;
    }

    if (!confirm(`Möchten Sie für alle Positionen dieser Bestellung Genehmigungsflows mit der Abteilung "${selectedDepartment}" erstellen?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await approvalFlowApi.createFlowForBestellung(bestellungId, selectedDepartment, user?.email);
      await loadFlows();
      if (onApprovalChange) {
        onApprovalChange();
      }
      setSelectedDepartment('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Erstellen der Flows');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (flowId: number) => {
    if (!user?.email) {
      setError('Benutzer-ID nicht verfügbar');
      return;
    }

    try {
      setActionInProgress(flowId);
      setError(null);
      await approvalFlowApi.approveStep(flowId, user?.email || '', comments[flowId], 'Department');
      await loadFlows();
      if (onApprovalChange) {
        onApprovalChange();
      }
      setComments((prev) => ({ ...prev, [flowId]: '' }));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Genehmigen');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (flowId: number) => {
    if (!user?.email) {
      setError('Benutzer-ID nicht verfügbar');
      return;
    }

    if (!comments[flowId]) {
      setError('Bitte geben Sie einen Ablehnungsgrund an');
      return;
    }

    try {
      setActionInProgress(flowId);
      setError(null);
      await approvalFlowApi.rejectStep(flowId, user?.email || '', comments[flowId], 'Department');
      await loadFlows();
      if (onApprovalChange) {
        onApprovalChange();
      }
      setComments((prev) => ({ ...prev, [flowId]: '' }));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Ablehnen');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleApproveGF = async (positionFlows: IApprovalFlow[], positionKey: string) => {
    if (!user?.email) {
      setError('Benutzer-ID nicht verfügbar');
      return;
    }

    try {
      setGfActionInProgress(positionKey);
      setError(null);
      for (const flow of positionFlows) {
        // Server-seitig berechnetes Feld nutzen statt E-Mail-Vergleich
        if (flow.CurrentUserCanApproveGF) {
          await approvalFlowApi.approveStep(flow.Id!, user.email, gfComments[positionKey], 'Management');
        }
      }
      await loadFlows();
      if (onApprovalChange) onApprovalChange();
      setGfComments(prev => ({ ...prev, [positionKey]: '' }));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Genehmigen');
    } finally {
      setGfActionInProgress(null);
    }
  };

  const handleRejectGF = async (positionFlows: IApprovalFlow[], positionKey: string) => {
    if (!user?.email) {
      setError('Benutzer-ID nicht verfügbar');
      return;
    }

    if (!gfComments[positionKey]) {
      setError('Bitte geben Sie einen Ablehnungsgrund an');
      return;
    }

    try {
      setGfActionInProgress(positionKey);
      setError(null);
      for (const flow of positionFlows) {
        // Server-seitig berechnetes Feld nutzen statt E-Mail-Vergleich
        if (flow.CurrentUserCanApproveGF) {
          await approvalFlowApi.rejectStep(flow.Id!, user.email, gfComments[positionKey], 'Management');
        }
      }
      await loadFlows();
      if (onApprovalChange) onApprovalChange();
      setGfComments(prev => ({ ...prev, [positionKey]: '' }));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Ablehnen');
    } finally {
      setGfActionInProgress(null);
    }
  };

  const handleDeleteFlow = async (flowId: number) => {
    if (!confirm('Möchten Sie den Genehmigungsflow wirklich löschen? Die Abteilung kann dann neu zugewiesen werden.')) {
      return;
    }

    try {
      setActionInProgress(flowId);
      setError(null);
      await approvalFlowApi.deleteFlow(flowId);
      await loadFlows();
      if (onApprovalChange) {
        onApprovalChange();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Löschen des Flows');
    } finally {
      setActionInProgress(null);
    }
  };

  const getStatusIcon = (status: IApprovalStep['Status']) => {
    switch (status) {
      case 'Freigegeben':
        return { iconName: 'CompletedSolid', color: '#107c10' };
      case 'Abgelehnt':
        return { iconName: 'StatusErrorFull', color: '#d13438' };
      case 'Offen':
        return { iconName: 'Clock', color: '#605e5c' };
      default:
        return { iconName: 'Help', color: '#605e5c' };
    }
  };

  const getOverallStatusColor = (status: IApprovalFlow['OverallStatus']) => {
    switch (status) {
      case 'Freigegeben': return '#107c10';
      case 'Abgelehnt': return '#d13438';
      case 'Teilweise freigegeben': return '#ff8c00';
      case 'Offen': return '#605e5c';
      default: return '#605e5c';
    }
  };

  // Rendert einen einzelnen Schritt (für Department und GF Boxen)
  const renderStep = (step: IApprovalStep, index: number) => {
    const stepStatusIcon = getStatusIcon(step.Status);
    return (
      <Stack
        key={index}
        horizontal
        tokens={{ childrenGap: 12 }}
        verticalAlign="center"
        styles={{ root: { padding: 8, backgroundColor: '#faf9f8', borderRadius: 4 } }}
      >
        <Icon iconName={stepStatusIcon.iconName} styles={{ root: { fontSize: 16, color: stepStatusIcon.color } }} />
        <Stack styles={{ root: { flexGrow: 1 } }}>
          <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>
            {step.ApproverName}
          </Text>
          <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
            {step.ApproverEmail}
          </Text>
          {step.Comments && (
            <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>
              Kommentar: {step.Comments}
            </Text>
          )}
          {step.ApprovedDate && (
            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
              {new Date(step.ApprovedDate).toLocaleString('de-DE')}
            </Text>
          )}
        </Stack>
        <Text variant="small" styles={{ root: { color: stepStatusIcon.color, fontWeight: 600 } }}>
          {step.Status}
        </Text>
      </Stack>
    );
  };

  // Rendert einen Abteilungs-Flow (nur Department-Schritte)
  const renderDepartmentFlow = (flow: IApprovalFlow) => {
    const deptSteps = flow.Steps.filter(s => s.StepType === 'Department');
    // Server-seitig berechnetes Feld nutzen (verhindert Probleme bei E-Mail-Domain-Abweichungen)
    const canApprove = flow.CurrentUserCanApprove === true;
    const statusIcon = getStatusIcon(flow.OverallStatus as any);

    return (
      <Stack
        key={flow.Id}
        tokens={{ childrenGap: 10 }}
        styles={{ root: { padding: 14, border: '1px solid #edebe9', borderRadius: 4, backgroundColor: '#ffffff' } }}
      >
        {/* Flow Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Icon
              iconName={statusIcon.iconName}
              styles={{ root: { fontSize: 18, color: getOverallStatusColor(flow.OverallStatus) } }}
            />
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
              {flow.DepartmentName}
            </Text>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Text variant="small" styles={{ root: { color: getOverallStatusColor(flow.OverallStatus), fontWeight: 600 } }}>
              {flow.OverallStatus}
            </Text>
            {(user?.permissions?.isAdmin || user?.permissions?.isEinkauf) && (
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Flow entfernen"
                ariaLabel="Flow entfernen"
                styles={{ root: { color: '#d13438', height: 24 } }}
                onClick={() => handleDeleteFlow(flow.Id!)}
                disabled={actionInProgress !== null}
              />
            )}
          </Stack>
        </Stack>

        {/* Action Area für Department-Genehmiger — oben */}
        {canApprove && (flow.OverallStatus === 'Offen' || flow.OverallStatus === 'Teilweise freigegeben') && (
          <Stack tokens={{ childrenGap: 8 }}>
            <TextField
              placeholder="Kommentar (optional für Genehmigung, erforderlich für Ablehnung)"
              value={comments[flow.Id!] || ''}
              onChange={(_, value) => setComments(prev => ({ ...prev, [flow.Id!]: value || '' }))}
              multiline
              rows={2}
            />
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text="Genehmigen"
                iconProps={{ iconName: 'Accept' }}
                onClick={() => handleApprove(flow.Id!)}
                disabled={actionInProgress !== null}
              />
              <DefaultButton
                text="Ablehnen"
                iconProps={{ iconName: 'Cancel' }}
                onClick={() => handleReject(flow.Id!)}
                disabled={actionInProgress !== null}
              />
            </Stack>
            <Separator />
          </Stack>
        )}

        {/* Department Steps */}
        {deptSteps.length > 0 && (
          <Stack tokens={{ childrenGap: 6 }}>
            {deptSteps.map((step, idx) => renderStep(step, idx))}
          </Stack>
        )}
      </Stack>
    );
  };

  // Rendert die kombinierte Geschäftsführungs-Box für eine Position
  const renderGFBox = (positionFlows: IApprovalFlow[], positionKey: string) => {
    // Alle Management-Schritte aus allen Flows dieser Position sammeln
    const gfSteps: (IApprovalStep & { flowDepartment: string })[] = positionFlows.flatMap(flow =>
      flow.Steps
        .filter(s => s.StepType === 'Management')
        .map(s => ({ ...s, flowDepartment: flow.DepartmentName }))
    );

    if (gfSteps.length === 0) return null;

    // Server-seitig berechnetes Feld: CurrentUserCanApproveGF
    const canApproveGF = positionFlows.some(f => f.CurrentUserCanApproveGF === true);
    const gfOverallStatus = gfSteps.every(s => s.Status === 'Freigegeben')
      ? 'Freigegeben'
      : gfSteps.some(s => s.Status === 'Abgelehnt')
      ? 'Abgelehnt'
      : gfSteps.some(s => s.Status === 'Freigegeben')
      ? 'Teilweise freigegeben'
      : 'Offen';
    const statusColor = getOverallStatusColor(gfOverallStatus as any);

    return (
      <Stack
        tokens={{ childrenGap: 10 }}
        styles={{
          root: {
            padding: 14,
            border: `1px solid ${statusColor}40`,
            borderRadius: 4,
            backgroundColor: '#f9f6ff',
          },
        }}
      >
        {/* GF Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Icon iconName="People" styles={{ root: { fontSize: 18, color: '#5c2d91' } }} />
            <Text variant="medium" styles={{ root: { fontWeight: 600, color: '#5c2d91' } }}>
              Geschäftsführung
            </Text>
          </Stack>
          <Text variant="small" styles={{ root: { color: statusColor, fontWeight: 600 } }}>
            {gfOverallStatus}
          </Text>
        </Stack>

        {/* Action Area für GF-Genehmiger — oben */}
        {canApproveGF && (
          <Stack tokens={{ childrenGap: 8 }}>
            <TextField
              label="Kommentar (optional für Genehmigung, erforderlich für Ablehnung)"
              placeholder="Ihr Kommentar..."
              value={gfComments[positionKey] || ''}
              onChange={(_, value) => setGfComments(prev => ({ ...prev, [positionKey]: value || '' }))}
              multiline
              rows={2}
            />
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text="Genehmigen"
                iconProps={{ iconName: 'Accept' }}
                onClick={() => handleApproveGF(positionFlows, positionKey)}
                disabled={gfActionInProgress !== null}
              />
              <DefaultButton
                text="Ablehnen"
                iconProps={{ iconName: 'Cancel' }}
                onClick={() => handleRejectGF(positionFlows, positionKey)}
                disabled={gfActionInProgress !== null}
              />
            </Stack>
            <Separator />
          </Stack>
        )}

        {/* GF Steps — dedupliziert nach Email */}
        <Stack tokens={{ childrenGap: 6 }}>
          {gfSteps
            .filter((step, idx, arr) => arr.findIndex(s => s.ApproverEmail.toLowerCase() === step.ApproverEmail.toLowerCase()) === idx)
            .map((step, idx) => renderStep(step, idx))
          }
        </Stack>
      </Stack>
    );
  };

  // Flows nach PositionId gruppieren
  const flowsByPosition = flows.reduce((acc, flow) => {
    const key = flow.PositionId != null ? String(flow.PositionId) : 'bestellung';
    if (!acc[key]) acc[key] = [];
    acc[key].push(flow);
    return acc;
  }, {} as Record<string, IApprovalFlow[]>);

  const positionKeys = Object.keys(flowsByPosition).sort((a, b) => {
    if (a === 'bestellung') return -1;
    if (b === 'bestellung') return 1;
    return parseInt(a) - parseInt(b);
  });

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      styles={centeredModalStyles}
    >
      {/* Header */}
      <div style={modalContentStyles.header}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          Genehmigungsflows
        </Text>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Schließen"
          onClick={onDismiss}
        />
      </div>

      {/* Body */}
      <div style={modalContentStyles.body}>
        <Stack tokens={{ childrenGap: 16 }}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
              {error}
            </MessageBar>
          )}

          {/* Admin/Einkauf können Flows für alle Positionen einer Abteilung erstellen */}
          {(user?.permissions?.isAdmin || user?.permissions?.isEinkauf) && (
            <Stack
              tokens={{ childrenGap: 12 }}
              styles={{ root: { padding: 16, backgroundColor: '#f3f2f1', borderRadius: 4, border: '1px solid #edebe9' } }}
            >
              <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                Abteilung hinzufügen (alle Positionen)
              </Text>
              <Dropdown
                placeholder="Abteilung auswählen"
                options={departments.map(d => ({ key: d.Department, text: d.Department }))}
                selectedKey={selectedDepartment}
                onChange={(_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) =>
                  setSelectedDepartment(option?.key as string)
                }
              />
              <PrimaryButton
                text="Flows erstellen"
                iconProps={{ iconName: 'Add' }}
                onClick={handleCreateFlowsForAll}
                disabled={!selectedDepartment || loading}
              />
              <MessageBar messageBarType={MessageBarType.info}>
                Erstellt Genehmigungsflows für alle Positionen mit der gewählten Abteilung. Bereits vorhandene Flows für dieselbe Abteilung werden übersprungen.
              </MessageBar>
            </Stack>
          )}

          <Separator />

          {loading ? (
            <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
              <Spinner label="Lade Genehmigungsflows..." />
            </Stack>
          ) : flows.length === 0 ? (
            <MessageBar messageBarType={MessageBarType.info}>
              Keine Genehmigungsflows vorhanden.
            </MessageBar>
          ) : (
            <Stack tokens={{ childrenGap: 20 }}>
              {positionKeys.map(positionKey => {
                const posFlows = flowsByPosition[positionKey];
                const deptFlows = posFlows; // alle Flows für die Position (Department-Box zeigt nur Dept-Steps)
                const label = positionKey === 'bestellung' ? 'Bestellung' : `Position ${positionKey}`;

                return (
                  <Stack key={positionKey} tokens={{ childrenGap: 10 }}>
                    {/* Positions-Header */}
                    <Text
                      variant="mediumPlus"
                      styles={{ root: { fontWeight: 700, color: '#323130', borderBottom: '2px solid #edebe9', paddingBottom: 4 } }}
                    >
                      {label}
                    </Text>

                    {/* Abteilungs-Flows */}
                    {deptFlows.map(flow => renderDepartmentFlow(flow))}

                    {/* Geschäftsführungs-Box (einmal pro Position) */}
                    {renderGFBox(posFlows, positionKey)}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </div>

      {/* Footer */}
      <div style={modalContentStyles.footer}>
        <DefaultButton text="Schließen" onClick={onDismiss} />
      </div>
    </Modal>
  );
};
